import { Check, RotateCcw, Save, Settings2 } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { SubmitButtons } from '@unistocks/components/button/submit-buttons';
import { Input } from '@unistocks/components/input/input';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Loader } from '@unistocks/components/loading/spinner/loader';
import { FullScreenSheet } from '@unistocks/components/sheet/full-screen-sheet';
import { Heading } from '@unistocks/components/text/text';
import { toast } from '@unistocks/components/toast';
import { FeatureDto, FeatureGroupDto, UpdateFeatureDto } from '@unistocks/proxy/axios-client';
import {
  useFeaturesGetQuery,
  useFeaturesUpdateMutation
} from '@unistocks/proxy/axios-client/FeaturesQuery';
import { t } from '@unistocks/shared/localization/index.ts';
import React, { useState } from 'react';
import { Checkbox, Label, Separator } from 'tamagui';

type FeatureManagementSheetProps = {
  providerName: string;
  providerKey: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type FeatureState = Record<string, string>;

const isToggleFeature = (feature: FeatureDto) =>
  feature.valueType?.name === 'ToggleStringValueType';

export const FeatureManagementSheet: React.FC<FeatureManagementSheetProps> = ({
  providerName,
  providerKey,
  open,
  onOpenChange
}) => {
  const queryClient = useQueryClient();
  const { data, isPending } = useFeaturesGetQuery(providerName, providerKey, {
    enabled: open
  });
  const { mutateAsync: updateFeatures, isPending: isSaving } = useFeaturesUpdateMutation(
    providerName,
    providerKey,
    {
      onSuccess: () => {
        toast.success(t('AbpUi::SavedSuccessfully'));
        queryClient.invalidateQueries({ queryKey: ['featuresGet'] });
        onOpenChange(false);
      }
    }
  );

  const [featureValues, setFeatureValues] = useState<FeatureState>({});
  const [prevData, setPrevData] = useState(data);

  if (prevData !== data) {
    setPrevData(data);

    if (data?.groups) {
      const initial: FeatureState = {};
      data.groups.forEach((group: FeatureGroupDto) => {
        group.features?.forEach((feature: FeatureDto) => {
          initial[feature.name!] = feature.value ?? '';
        });
      });
      setFeatureValues(initial);
    }
  }

  const setFeatureValue = (name: string, value: string) => {
    setFeatureValues(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    const features: UpdateFeatureDto[] = Object.entries(featureValues).map(([name, value]) => ({
      name,
      value
    }));
    updateFeatures({ features });
  };

  return (
    <FullScreenSheet
      open={open}
      onOpenChange={onOpenChange}
      headerTitle={t('AbpFeatureManagement::ManageHostFeatures')}
      headerIcon={<Settings2 color={'$unistocks'} />}
    >
      {isPending ? (
        <Loader />
      ) : (
        <Column gap={'$3'}>
          {data?.groups?.map((group: FeatureGroupDto) => (
            <Column key={group.name} gap={'$2'}>
              <Heading size={'$6'}>{group.displayName}</Heading>
              <Separator />

              {group.features?.map((feature: FeatureDto) => (
                <Column key={feature.name} gap={'$1'} pl={feature.depth ? '$4' : undefined}>
                  {isToggleFeature(feature) ? (
                    <Row gap={'$3'} alignItems="center">
                      <Checkbox
                        id={`feature-${feature.name}`}
                        checked={featureValues[feature.name!] === 'true'}
                        onCheckedChange={checked =>
                          setFeatureValue(feature.name!, checked ? 'true' : 'false')
                        }
                      >
                        <Checkbox.Indicator>
                          <Check />
                        </Checkbox.Indicator>
                      </Checkbox>
                      <Label htmlFor={`feature-${feature.name}`}>{feature.displayName}</Label>
                    </Row>
                  ) : (
                    <Column gap={'$1'}>
                      <Label>{feature.displayName}</Label>
                      <Input
                        value={featureValues[feature.name!] ?? ''}
                        onChangeText={text => setFeatureValue(feature.name!, text)}
                      />
                    </Column>
                  )}
                </Column>
              ))}
            </Column>
          ))}

          <Separator my={'$2'} />

          <SubmitButtons
            cancelButtonText={t('AbpUi::Cancel')}
            cancelButtonIcon={RotateCcw}
            onCancel={() => onOpenChange(false)}
            submitButtonText={t('AbpUi::Save')}
            submitButtonIcon={Save}
            onSubmit={handleSave}
            isSubmitting={isSaving}
          />
        </Column>
      )}
    </FullScreenSheet>
  );
};

FeatureManagementSheet.displayName = 'FeatureManagementSheet';
